import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import { DataAccessExhibitorService } from '@planone/bau365/data-access-fair-data';
import { subscribeChannelSuccess, unsubscribeChannelSuccess } from '@planone/bau365/data-access-user';
import { Exhibitor } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { combineLatestWith, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { subscribe } from './subscribe.actions';

import { ExhibitorSubscribeEventInterface } from './subscribe.models';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class SubscribeEffects {
  subscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscribeChannelSuccess),
      map(({ channel, context }) =>
        subscribe({
          entityType: 'EXHIBITOR',
          exhibitorId: channel.id,
          context: channel.fairId,
          pageType: context,
          method: 'SUBSCRIBE',
        })
      )
    )
  );

  unsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(unsubscribeChannelSuccess),
      map(({ channel, context }) =>
        subscribe({
          entityType: 'EXHIBITOR',
          exhibitorId: channel.id,
          context: channel.fairId,
          pageType: context,
          method: 'UNSUBSCRIBE',
        })
      )
    )
  );

  exhibitorSubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscribe),
      filter((action) => action.entityType === 'EXHIBITOR'),
      switchMap((action) =>
        of(action.exhibitorId).pipe(
          combineLatestWith(this.exhibitorsService.getExhibitor(action.context, action.exhibitorId)),
          filter(([_, exhibitor]) => !!exhibitor),
          map(([_, exhibitor]) => exhibitor as any),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, excludeAnalytics]) =>
              <[ExhibitorSubscribeEventInterface, Exhibitor, boolean]>[action, exhibitor, excludeAnalytics]
          ),
          switchMap(([action, exhibitor, excludeAnalytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowContext = [];
            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.subscribe,
                  data: {
                    method: action.method,
                    entity_type: action.entityType,
                    context: action.context,
                    page_type: action.pageType,
                    exclude_analytics: excludeAnalytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'perform_subscribe',
                method: action.method,
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: excludeAnalytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private authenticationFacade: AuthenticationFacade,
    private exhibitorsService: DataAccessExhibitorService,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
